import React, { FC, useEffect, useState } from 'react';
import Button from 'components/revelo-ui/content/button';
import { useFormat } from 'helpers/hooks/useFormat';
import { useAccount } from 'frontastic';
import ChevronLeftIcon from 'components/icons/chevron-left';
import EditIcon from 'components/icons/edit';
import UserIcon from 'components/icons/user';
import EnvelopeIcon from 'components/icons/envelope';
import PhoneIcon from 'components/icons/phone';
import classNames from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import PersonalDataForm from '../forms/personalDataForm';
import AddressForm from '../forms/addressForm';
import FormFieldCheckbox from '../../../forms/form-field-checkbox';
import { Address } from '@Types/account/Address';
import { useCountries } from '../../../../../frontastic/actions/config';
import { AddressUtils } from '../../../utils/AddressUtils';
import { countryOptions } from 'helpers/countryOptions';

export interface Props {
  onCloseContent?: () => void;
}

const PersonalData: FC<Props> = ({ onCloseContent }) => {
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { account, updateAddresses, removeAddress } = useAccount();
  const { billingCountries, shippingCountries } = useCountries();
  const formMethods = useForm({
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const [editModePersonal, setEditModePersonal] = useState<boolean>(false);
  const [editModeAddresses, setEditModeAddresses] = useState<boolean>(false);
  const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState<boolean>(
    account.addresses.length === 0 ||
      !!account?.addresses.find((address) => address.isDefaultShippingAddress && address.isDefaultBillingAddress),
  );
  const [defaultBillingAddress, setDefaultBillingAddress] = useState<Address>({});
  const [defaultShippingAddress, setDefaultShippingAddress] = useState<Address>({});

  const boxClasses = 'flex flex-col gap-4 bg-neutral-50 p-4';
  const iconTextClasses = 'flex items-center gap-2';

  const onSubmit = async (formData) => {
    const addresses = [
      {
        ...defaultShippingAddress,
        firstName: formData.shipping.firstName,
        lastName: formData.shipping.lastName,
        streetName: formData.shipping.streetName,
        streetNumber: formData.shipping.streetNumber,
        postalCode: formData.shipping.postalCode,
        city: formData.shipping.city,
        additionalData: formData.shipping.additionalStreetInfo,
        company: formData.shipping.company,
        isDefaultBillingAddress: formData.billingSameAsShipping,
        isDefaultShippingAddress: true,
        country: formData.shipping.country,
      },
    ];
    if (formData.billingSameAsShipping === false) {
      addresses.push({
        ...defaultBillingAddress,
        firstName: formData.billing.firstName,
        lastName: formData.billing.lastName,
        streetName: formData.billing.streetName,
        streetNumber: formData.billing.streetNumber,
        postalCode: formData.billing.postalCode,
        city: formData.billing.city,
        additionalData: formData.billing.additionalStreetInfo,
        company: formData.billing.company,
        isDefaultBillingAddress: !formData.billingSameAsShipping,
        isDefaultShippingAddress: false,
        country: formData.billing.country,
      });
    }

    try {
      await updateAddresses(addresses).then((response) => {
        response.addresses
          .filter((address) => address.isDefaultBillingAddress === false && address.isDefaultShippingAddress === false)
          .map((address) => removeAddress(address.addressId));
      });
    } catch (error) {
      console.error('Error while updating addresses:', error);
    } finally {
      setEditModeAddresses(false);
    }
  };

  useEffect(() => {
    const shippingAddress = account?.addresses.find((address) => address.isDefaultShippingAddress);
    const billingAddress = shippingAddress?.isDefaultBillingAddress
      ? {}
      : account?.addresses.find((address) => address.isDefaultBillingAddress);

    setDefaultShippingAddress(shippingAddress ?? {});
    setDefaultBillingAddress(billingAddress ?? {});
  }, [account, isBillingSameAsShipping]);

  return (
    <div>
      <h3 className="mb-4 flex items-center gap-2 font-headline text-xl text-neutral-950">
        <button onClick={onCloseContent} className="lg:hidden">
          <ChevronLeftIcon className="size-6" />
        </button>
        {formatAccountMessage({
          id: 'personal.data.headline',
          defaultMessage: 'Meine Daten',
        })}
      </h3>
      <div className="flex flex-col gap-8">
        <div className={classNames(boxClasses)}>
          <div className="flex justify-between">
            <div className="font-headline text-lg">
              {formatAccountMessage({
                id: 'personal.headline',
                defaultMessage: 'Persönliche Informationen',
              })}
            </div>
            {!editModePersonal && (
              <button onClick={() => setEditModePersonal(true)}>
                <EditIcon className="size-6 text-neutral-950" />
              </button>
            )}
          </div>
          {(account.firstName || account.lastName) && !editModePersonal && (
            <div className={classNames(iconTextClasses)}>
              <UserIcon className="size-6" />
              {account.firstName} {account.lastName}
            </div>
          )}
          <div className={classNames(iconTextClasses)}>
            <EnvelopeIcon className="size-6" />
            {account.email}
          </div>
          {defaultShippingAddress?.phone && !editModePersonal && (
            <div className={classNames(iconTextClasses)}>
              <PhoneIcon className="size-6" />
              {defaultShippingAddress.phone}
            </div>
          )}
          {editModePersonal && (
            <PersonalDataForm
              defaultBillingAddress={
                isBillingSameAsShipping || !defaultBillingAddress.isDefaultBillingAddress
                  ? undefined
                  : defaultBillingAddress
              }
              defaultShippingAddress={
                !defaultShippingAddress?.isDefaultShippingAddress ? undefined : defaultShippingAddress
              }
              closeEditMode={() => setEditModePersonal(false)}
            />
          )}
        </div>
        <div className={classNames(boxClasses)}>
          <div className="flex justify-between">
            <div className="font-headline text-lg">
              {formatAccountMessage({
                id: 'personal.addresses',
                defaultMessage: 'Meine Adressen',
              })}
            </div>
            {!editModeAddresses && (
              <button onClick={() => setEditModeAddresses(true)}>
                <EditIcon className="size-6 text-neutral-950" />
              </button>
            )}
          </div>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
              {account.addresses.length > 0 || editModeAddresses ? (
                <>
                  <div>
                    <div className="mb-2">
                      {formatAccountMessage({
                        id: 'personal.shippingAddress',
                        defaultMessage: 'Lieferadresse',
                      })}
                    </div>
                    {editModeAddresses ? (
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <AddressForm
                          address={defaultShippingAddress}
                          type="shipping"
                          countries={
                            isBillingSameAsShipping
                              ? AddressUtils.intersectCountryLists(billingCountries, shippingCountries)
                              : shippingCountries
                          }
                        />
                      </div>
                    ) : (
                      <div>
                        {defaultShippingAddress.firstName} {defaultShippingAddress.lastName}
                        <br />
                        {defaultShippingAddress.streetName} {defaultShippingAddress.streetNumber}
                        <br />
                        {defaultShippingAddress.postalCode} {defaultShippingAddress.city}
                        <br />
                        {countryOptions.find((option) => option.data === defaultShippingAddress.country)?.display}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="mb-2">
                      {formatAccountMessage({
                        id: 'personal.billingAddress',
                        defaultMessage: 'Rechnungsadresse',
                      })}
                    </div>
                    {(isBillingSameAsShipping || editModeAddresses) && (
                      <div className="mb-6 mt-4 flex items-center gap-2">
                        <FormFieldCheckbox
                          checked={isBillingSameAsShipping}
                          onChange={(checked) => {
                            if (checked !== undefined) {
                              setIsBillingSameAsShipping(checked);
                            }
                          }}
                          name="billingSameAsShipping"
                          label={formatAccountMessage({
                            id: 'billingAddressSameAsShipping',
                            defaultMessage: 'Die Rechnungsadresse entspricht der Lieferadresse',
                          })}
                          inverseLabel
                          containerClassNames="flex items-center gap-4 col-span-full pt-2"
                          labelClassNames={classNames(
                            'block text-sm text-neutral-950 dark:text-light-100',
                            editModeAddresses ? 'cursor-pointer' : 'cursor-default',
                          )}
                          disabled={!editModeAddresses}
                        />
                      </div>
                    )}
                    {!isBillingSameAsShipping && (
                      <>
                        {editModeAddresses ? (
                          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <AddressForm address={defaultBillingAddress} type="billing" countries={billingCountries} />
                          </div>
                        ) : (
                          <div>
                            {defaultBillingAddress.streetName} {defaultBillingAddress.streetNumber}
                            <br />
                            {defaultBillingAddress.postalCode} {defaultBillingAddress.city}
                            <br />
                            {countryOptions.find((option) => option.data === defaultBillingAddress.country)?.display}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  {formatAccountMessage({
                    id: 'personal.no.addresses',
                    defaultMessage: 'Es sind noch keine Adressen hinterlegt.',
                  })}
                </div>
              )}

              {editModeAddresses && (
                <div>
                  <Button style="secondary" type="submit" disabled={!isValid}>
                    {formatAccountMessage({
                      id: 'personal.save.changes',
                      defaultMessage: 'Änderungen speichern',
                    })}
                  </Button>
                </div>
              )}
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default PersonalData;
